import type * as React from 'react'
import { useEffect } from 'react'

export type UseScrollFlashOptions = {
  /**
   * Whether the flash should run. Toggling from `false` to `true`
   * triggers a new flash.
   * @default true
   */
  enabled?: boolean
}

/**
 * Briefly nudges the scroll position of a scrollable container so its
 * scrollbar flashes, hinting that there is more content available.
 *
 * The nudge fires a scroll event, so it also reveals scrollbars that are
 * hidden until the user scrolls (e.g. macOS overlay scrollbars or
 * `ScrollArea` with `visibility="scroll"` / `visibility="hover"`).
 * Containers without overflowing content don't scroll, so no hint is
 * shown when there is nothing to reveal.
 *
 * @param scrollContainerRef - A ref to the scrollable element
 * @param options.enabled - Set to false to skip the flash (default: true)
 *
 * @example
 * const scrollContainerRef = React.useRef<HTMLDivElement>(null)
 * useScrollFlash(scrollContainerRef)
 *
 * return (
 *   <div ref={scrollContainerRef} style={{ overflowY: 'auto' }}>
 *     {content}
 *   </div>
 * )
 */
export const useScrollFlash = (
  scrollContainerRef: React.RefObject<HTMLElement>,
  { enabled = true }: UseScrollFlashOptions = {},
): void => {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!enabled || !scrollContainer) {
      return
    }

    let restoreFrameId: number | undefined

    // Defer to the next frame to avoid forcing a synchronous reflow
    // right after React commits DOM changes.
    const nudgeFrameId = requestAnimationFrame(() => {
      const currentScroll = scrollContainer.scrollTop
      // Nudge towards the direction that is guaranteed to be scrollable:
      // an already-scrolled container may be at the bottom edge, where
      // scrolling further down is a no-op and nothing would flash.
      const nudgedScroll =
        currentScroll < 1 ? currentScroll + 1 : currentScroll - 1

      scrollContainer.scrollTop = nudgedScroll

      if (scrollContainer.scrollTop === currentScroll) {
        return
      }

      // Restore on the following frame so browsers and Radix ScrollArea can
      // observe a real scroll position change before the position is reset.
      restoreFrameId = requestAnimationFrame(() => {
        if (scrollContainer.scrollTop === nudgedScroll) {
          scrollContainer.scrollTop = currentScroll
        }
      })
    })

    return () => {
      cancelAnimationFrame(nudgeFrameId)

      if (restoreFrameId !== undefined) {
        cancelAnimationFrame(restoreFrameId)
      }
    }
  }, [scrollContainerRef, enabled])
}
